import { KeyboardEvent } from "react";
import { FormattedMessage } from "react-intl";
import { get, useFormContext } from "react-hook-form";

import { stepSpecialRequirementNumeric } from "helpers/validationHelpers";

type NumericSpinnerFieldProps = {
  fieldId: string;
  dataType: "integer" | "double";
  mandatory?: boolean;
  placeholder?: string;
  containerClasses?: string;
  validations?: Record<string, unknown>;
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
  id?: string;
};

function NumericSpinnerField({
  fieldId,
  dataType,
  mandatory = false,
  placeholder = "",
  containerClasses = "",
  validations,
  onKeyPress,
  id = "",
}: NumericSpinnerFieldProps) {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const value = watch(fieldId) ?? "";
  const fieldError = get(errors, fieldId);
  const testId = `form-field-${fieldId.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}`;

  const updateValue = (nextValue: string) => {
    setValue(fieldId, nextValue, { shouldValidate: true, shouldDirty: true });
  };

  const handleStep = (direction: 1 | -1) => {
    updateValue(stepSpecialRequirementNumeric(value, direction, dataType));
  };

  const { onChange, onBlur, name, ref } = register(fieldId, {
    required: mandatory,
    ...validations,
  });

  return (
    <div
      className={`d-flex flex-column numeric-spinner-field ${containerClasses}`}
      id={fieldId}
      data-testid={testId}
    >
      <div className="numeric-spinner-field__input-wrap">
        <input
          ref={ref}
          name={name}
          id={id}
          type="text"
          inputMode="decimal"
          placeholder={placeholder}
          value={value}
          className={`form-control font-b3 br-10 pt-6 pb-6 pl-10 pr-10 bg-white w-100 height-auto placeholder-light ${
            fieldError ? "invalid-input" : ""
          }`}
          data-testid={`${testId}-input`}
          onChange={onChange}
          onBlur={onBlur}
          onKeyPress={onKeyPress}
        />
        <div className="numeric-spinner-field__buttons">
          <button
            type="button"
            className="numeric-spinner-field__button"
            aria-label="Decrease value"
            onClick={() => handleStep(-1)}
          >
            -
          </button>
          <button
            type="button"
            className="numeric-spinner-field__button"
            aria-label="Increase value"
            onClick={() => handleStep(1)}
          >
            +
          </button>
        </div>
      </div>
      {fieldError ? (
        <div className="d-block font-b3 invalid-feedback">
          {fieldError.message ? (
            fieldError.message
          ) : (
            <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
          )}
        </div>
      ) : null}
    </div>
  );
}

export default NumericSpinnerField;
